import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  OrganizationRoleWithRelationsDto,
  ResponseDto,
  CreateOrganizationRoleDto,
} from "@mimber/schema"
import { rolesKeys } from "../roles.utils"

/**
 * Organization-specific hook for creating a new organization role
 * Can only be used in mimber-organization app
 */
export const useCreateRole = (
  options?: UseMutationOptions<
    ResponseDto<OrganizationRoleWithRelationsDto>,
    Error,
    CreateOrganizationRoleDto
  >
): UseMutationResult<
  ResponseDto<OrganizationRoleWithRelationsDto>,
  Error,
  CreateOrganizationRoleDto
> => {
  guardAppUsage("mimber-organization", "useCreateRole")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (createDto: CreateOrganizationRoleDto) => {
      const response = await server.post<
        ResponseDto<OrganizationRoleWithRelationsDto>
      >("roles", createDto)
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.findAll() })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
